// Structured "patch notes" format stored (JSON-encoded) in FirmwareBase.changelog.
//
// The mobile app must only render patch notes for a changelog string that
// parses as JSON AND matches this exact shape (format === PATCH_NOTES_FORMAT,
// items a non-empty array). Anything else - null, plain free text left over
// from before this format existed, malformed JSON, a future/unknown format
// value - must be treated as "no patch notes to show," never rendered as a
// raw string. That's what makes this backward compatible with every
// pre-existing changelog value without a migration: they simply fail the
// parse/shape check and are hidden.

export const PATCH_NOTES_FORMAT = "canny_patch_notes_v1" as const;

export type PatchNoteItemType = "feature" | "fix" | "improvement";

export interface PatchNoteItem {
  type: PatchNoteItemType;
  text: string;
}

export interface PatchNotes {
  format: typeof PATCH_NOTES_FORMAT;
  summary?: string;
  items: PatchNoteItem[];
}

export const PATCH_NOTE_ITEM_META: Record<PatchNoteItemType, { label: string; icon: string; color: string }> = {
  feature: { label: "New", icon: "✨", color: "#52c41a" },
  fix: { label: "Fixed", icon: "🐛", color: "#ff4d4f" },
  improvement: { label: "Improved", icon: "⚡", color: "#1677ff" },
};

export const emptyPatchNotes = (): PatchNotes => ({
  format: PATCH_NOTES_FORMAT,
  summary: "",
  items: [{ type: "feature", text: "" }],
});

// Returns the parsed patch notes only if `raw` matches the structured format
// exactly (same rule the mobile app is expected to apply) - null otherwise,
// including for legacy free-text changelogs.
export const parsePatchNotes = (raw: string | null | undefined): PatchNotes | null => {
  if (!raw) return null;
  let parsed: any;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (
    !parsed ||
    typeof parsed !== "object" ||
    parsed.format !== PATCH_NOTES_FORMAT ||
    !Array.isArray(parsed.items) ||
    parsed.items.length === 0 ||
    !parsed.items.every(
      (item: any) =>
        item &&
        typeof item.text === "string" &&
        ["feature", "fix", "improvement"].includes(item.type)
    )
  ) {
    return null;
  }
  return parsed as PatchNotes;
};

export const serializePatchNotes = (notes: PatchNotes): string => JSON.stringify(notes);

// A structured PatchNotes is only actually meaningful once it has at least
// one non-empty item - used to decide whether the "Save" action should be
// allowed to submit it as structured vs. treating it as still-empty.
export const hasContent = (notes: PatchNotes): boolean =>
  notes.items.some((item) => item.text.trim().length > 0);
